import { AppBar, Box, Tab, Tabs } from "@mui/material";
import { withStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { SUBNAV_ARIA_LABEL } from "../../constants";
import { ediphiPalette } from "../../styles/colors";

// Custom Tab to remove text transformation (uppercase)
/** Unselected tab label. Paired with the theme's `text.primary` for the selected one. */
const INACTIVE_TAB_COLOR = "#9e9e9e";

const CustomTab = withStyles({
  root: {
    textTransform: "none",
    fontWeight: 500,
    width: "auto",
    minWidth: "auto",
    padding: "0 10px",
    margin: 0,
    "& .MuiTab-wrapper": {
      flexDirection: "column",
      alignItems: "center",
    },
  },
})(Tab);

interface SubnavProps {
  buttons: { name: string; icon: JSX.Element }[];
  activePage: string;
  onButtonClick: (name: string) => void;
}

const Subnav: React.FC<SubnavProps> = ({ buttons, activePage, onButtonClick }) => {
  // Find the initial index for the activePage (findIndex returns -1 when no match)
  const indexForPage = (page: string) => {
    const i = buttons.findIndex((button) => button.name === page);
    return i >= 0 ? i : 0; // return 0 if no match found
  };
  const [value, setValue] = useState<number>(() => indexForPage(activePage));

  // Update tab index when activePage changes
  useEffect(() => {
    const newIndex = indexForPage(activePage);
    if (newIndex !== value) {
      setValue(newIndex);
    }
  }, [activePage, buttons, value]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    onButtonClick(buttons[newValue].name);
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        padding: "0 2px 0",
      }}
    >
      <AppBar
        position="static"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center", // Align items in the center vertically
          backgroundColor: ediphiPalette.surface,
          borderBottom: "2px solid",
          borderColor: "divider",
          boxShadow: "none",
          paddingLeft: "10px", // Add padding if necessary
        }}
      >
        <Box
          component="img"
          src="assets/ediphi_logo.svg"
          alt="Ediphi Logo"
          sx={{
            height: "100%", // Adjust height for logo
            width: "auto",
            padding: "12px 12px 12px 0",
          }}
        />
        <Tabs value={value} onChange={handleChange} variant="standard" centered aria-label={SUBNAV_ARIA_LABEL}>
          {buttons.map((button, index) => (
            <CustomTab
              key={button.name}
              icon={
                <Box
                  component="span"
                  sx={{
                    fontSize: "10px",
                    fontWeight: "lighter",
                    color: index === value ? "text.primary" : INACTIVE_TAB_COLOR,
                    transform: "scale(0.8)",
                    display: "inline-block",
                  }}
                >
                  {button.icon}
                </Box>
              }
              label={button.name}
              disableRipple
            />
          ))}
        </Tabs>
      </AppBar>
    </Box>
  );
};

export default Subnav;
